import type { ContactFormStep } from './contactForm/constants';
import type { ExternalFormSubmissionResult } from './contactForm/useContactFormActions';
import type { User } from '#auth-utils';

import { useLeadFlowLogger } from '~/composables/contactForm/useLeadFlowLogger';
import { extractErrorLogContext } from '~/composables/useLogger';
import { EventBusKeys } from '~/core/helpers/symbols';

interface ExternalHandlerContext {
	isOpen: Ref<boolean>;
	currentStep: Ref<ContactFormStep>;
	isUserLoggedIn: ComputedRef<boolean>;
	user: ComputedRef<User | null>;
	goToOtpFormStep: (shouldSendCode?: boolean) => void;
	sendForm: () => Promise<void>;
	submitExternalForm: () => Promise<ExternalFormSubmissionResult>;
	addEventError: () => void;
}

export function useContactFormExternalHandler(context: ExternalHandlerContext) {
	const {
		isOpen,
		currentStep,
		isUserLoggedIn,
		user,
		goToOtpFormStep,
		sendForm,
		submitExternalForm,
		addEventError
	} = context;

	const logger = useLeadFlowLogger('ContactFormExternalHandler');

	async function handleExternalFormSubmission() {
		const isUserPhoneAlreadyValidated: boolean = !!(
			user.value?.phoneNumber && user.value.hasCompletedRegistration
		);

		logger.info('handleExternalFormSubmission disparado', {
			isUserLoggedIn: isUserLoggedIn.value,
			isUserPhoneAlreadyValidated,
			currentStep: currentStep.value,
			wasOpen: isOpen.value
		});

		try {
			if (!isOpen.value) {
				isOpen.value = true;
				await nextTick();
			}

			if (isUserLoggedIn.value && isUserPhoneAlreadyValidated) {
				const result = await submitExternalForm();

				// Envio ja em andamento. Nao e falha, entao nao reporta erro.
				if (result.skipped) {
					return;
				}

				currentStep.value = result.step;

				await nextTick();

				// O watch(currentStep) do useContactFormEvents dispara o evento de
				// sucesso e o de erro na transicao de passo. Chamar addEventError
				// aqui dobrava form_property_error, porque submitLead ja passou por
				// goToErrorMessage antes de devolver a falha.
				if (!result.success) {
					logger.warn('submitExternalForm retornou falha', {
						step: result.step
					});
				}
			} else {
				await sendForm();
			}
		} catch (error) {
			logger.error('Exceção não esperada em handleExternalFormSubmission', {
				isUserLoggedIn: isUserLoggedIn.value,
				isUserPhoneAlreadyValidated,
				...extractErrorLogContext(error)
			});
			addEventError();
		}
	}

	onClientEventBus(
		useEventBus<void>(EventBusKeys.openContactModalAtOtp),
		handleExternalFormSubmission
	);

	return {
		handleExternalFormSubmission
	};
}
